
import React from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, Bell } from 'lucide-react';

interface GeneratedContentDisplayProps {
  isGenerating: boolean;
  generatedContent: string;
  contentTitle: string;
  setContentTitle: (title: string) => void;
  onSaveAsNotification: () => void;
}

const GeneratedContentDisplay: React.FC<GeneratedContentDisplayProps> = ({
  isGenerating,
  generatedContent,
  contentTitle,
  setContentTitle,
  onSaveAsNotification
}) => {
  if (!isGenerating && !generatedContent) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{isGenerating ? 'Generating Content...' : 'Generated Content'}</CardTitle>
        {!isGenerating && (
          <CardDescription>
            Review and save your AI-generated content
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        {isGenerating ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        ) : (
          <div className="rounded-md bg-muted p-4 text-left">
            <p className="whitespace-pre-wrap text-left">{generatedContent}</p>
          </div>
        )}
      </CardContent>
      {!isGenerating && generatedContent && (
        <CardFooter className="flex-col items-start gap-4">
          <div className="w-full">
            <Label htmlFor="content-title">Title (Optional)</Label>
            <Input
              id="content-title"
              placeholder="Give your content a title"
              value={contentTitle}
              onChange={(e) => setContentTitle(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="flex gap-2 w-full">
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={() => {
                navigator.clipboard.writeText(generatedContent);
                toast.success('Copied to clipboard');
              }}
            >
              <FileText className="h-4 w-4" />
              Copy
            </Button>
            <Button 
              className="flex items-center gap-2 flex-1"
              onClick={onSaveAsNotification}
            >
              <Bell className="h-4 w-4" />
              Save as Notification
            </Button>
          </div>
        </CardFooter>
      )}
    </Card>
  );
};

export default GeneratedContentDisplay;
